/* eslint-disable react/jsx-max-depth */
import {
	FaFacebookSquare, FaInstagramSquare, FaLine, FaTiktok,
} from 'react-icons/fa';
import {
	Button, ButtonGroup, Card, Flex, For, Stack, Text,
} from '@chakra-ui/react';

import { NextImageWithSkeleton } from '@packages/chakra-v3';
import { useLocale } from 'next-intl';
import type { ContactUsPage } from '@/modules/sanity-studio/types';
import { SocialMediaCard } from '../../components';
import { MdOutlineMailOutline } from 'react-icons/md';
import { LinkComponent } from '@/modules/navigation';
import { DefaultStyle } from '@packages/wastic-shared';

type ContactUsContainerProps = {
	contactUsPage: ContactUsPage;
};
const ContactUsContainer = ({ contactUsPage }: ContactUsContainerProps) => {
	const locale = useLocale() as 'th' | 'en';

	const socialMedia = [
		{
			icon:  <FaLine size={32} />,
			label: 'Line',
			link:  '@Wastic',
			href:  contactUsPage.followUs.line,
		},
		{
			icon:  <FaFacebookSquare size={32} />,
			label: 'Facebook',
			link:  'wasticofficial',
			href:  contactUsPage.followUs.facebook,
		},
		{
			icon:  <FaInstagramSquare size={32} />,
			label: 'Instagram',
			link:  '@wastic_official',
			href:  contactUsPage.followUs.instagram,
		},
		{
			icon:  <FaTiktok size={32} />,
			label: 'TikTok',
			link:  '@wasticofficial',
			href:  contactUsPage.followUs.tiktok,
		},
	];

	return (
		<Stack gap={DefaultStyle.SMALL_SECTION_GAP} layerStyle={'black'}>
			<Stack alignItems={'center'} gap={1}>
				<Text as={'h2'} textAlign={'center'} textStyle={'h2'}>
					{contactUsPage.title[locale]}
				</Text>
				<Text
					maxW={'80ch'}
					px={8}
					textAlign={'center'}
					textStyle={'p3'}
				>
					{contactUsPage.description[locale]}
				</Text>
			</Stack>
			<Card.Root
				display={'flex'}
				flexDirection={'row'}
				w={'100%'}
			>
				<Card.Body
					display={'flex'}
					flex={1}
					flexDirection={'row'}
					flexWrap={'wrap'}
					gap={DefaultStyle.LARGE_GAP}
					p={8}
				>
					<Stack flex={3} minW={'240px'}>
						<NextImageWithSkeleton
							alt={'contact us'}
							aspectRatio={'landscape'}
							src={contactUsPage.image?.asset.url ?? ''}
							width={'100%'}
						/>
					</Stack>
					<Stack
						alignContent={'center'}
						flex={2}
						gap={DefaultStyle.MEDIUM_GAP}
						justifyContent={'center'}
					>
						<Stack gap={2}>
							<Text textStyle={'h4'}>{contactUsPage.address.title[locale]}</Text>
							<Text textStyle={'p3'}>{contactUsPage.address.address[locale]}</Text>
						</Stack>
						<ButtonGroup
							flexWrap={'wrap-reverse'}
							gap={DefaultStyle.SMALL_GAP}
							w={'full'}
						>
							<Button
								asChild
								_icon={{ mb: 0.5 }}
								flex={1}
								pt={1}
							>
								<LinkComponent
									href={`mailto:${contactUsPage.contact.email}`}
								>
									<MdOutlineMailOutline />
									Email
								</LinkComponent>
							</Button>
						</ButtonGroup>
					</Stack>
				</Card.Body>
			</Card.Root>
			<Card.Root>
				<Card.Header>
					<Card.Title textAlign={'center'} textStyle={'h4'}>
						Connect with us
					</Card.Title>
					<Card.Description textAlign={'center'} textStyle={'p3'}>
						Follow us for the latest updates, sustainability tips, and style inspiration
					</Card.Description>
				</Card.Header>
				<Card.Body>
					<Flex
						flexWrap={'wrap'}
						gap={DefaultStyle.SMALL_GAP}
						justifyContent={'center'}
						wrap={'flex-wrap'}
					>
						<For each={socialMedia}>
							{social => (
								<SocialMediaCard
									key={social.label + social.link}
									aspectRatio={'landscape'}
									flex={1}
									href={social.href}
									icon={social.icon}
									label={social.label}
									link={social.link}
									minW={'120px'}
								/>
							)}
						</For>
					</Flex>
				</Card.Body>
			</Card.Root>
		</Stack>
	);
};

export default ContactUsContainer;